import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTooltip, useTooltipInPortal, defaultStyles } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

const tooltipStyles = {
  ...defaultStyles,
  backgroundColor: '#4b5563',
  color: 'white',
  fontSize: 12,
  zIndex: 30,
  maxWidth: 250,
};

interface TooltipData {
  description: string;
}

interface StepWrapperProps {
  title: string;
  subtitle: string;
  onBack: () => void;
  onContinue: () => void;
  continueDisabled?: boolean;
  isLoading?: boolean;
  isFailed?: boolean;
  errorMessage?: string;
  children: React.ReactNode;
  isLastStep?: boolean;
  finishEarlyAvailable?: boolean;
  finishEarlyContinueButtonText?: string;
  onFinishEarly?: () => void;
  customButtonLabel?: string;
  customButtonAction?: () => void;
  tooltip?: string;
}

export default function StepWrapper(props: StepWrapperProps): JSX.Element {
  const [historyIndex, setHistoryIndex] = useState(0);
  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, hideTooltip, showTooltip } = useTooltip<TooltipData>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    scroll: true,
  });

  useEffect(() => {
    if (window.history.state !== null) {
      setHistoryIndex(window.history.state.idx);
    }
  }, []);

  window.onpopstate = () => {
    if (window.history.state === null) {
      return;
    }

    if (window.history.state.idx < historyIndex) {
      props.onBack();
    } else {
      props.onContinue();
    }
  };

  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="h-full flex flex-col">
      <div className="w-3/4 mx-[auto] text-center pb-6 flex flex-col items-center">
        <h2 className="text-base font-semibold text-gray-900">{props.title}</h2>
        <p className="mt-1 text-sm text-gray-600">{props.subtitle}</p>
      </div>

      <div className="grow overflow-y-scroll pb-6">{props.children}</div>

      <div className="pt-4 flex items-center justify-center gap-x-6 border-t border-gray-300">
        <button
          type="button"
          className="text-sm font-semibold leading-6 text-gray-900 hover:underline"
          onClick={() => props.onBack()}
        >
          Back
        </button>
        {props.customButtonLabel !== undefined && props.customButtonAction !== undefined && (
          <div ref={props.tooltip !== undefined ? containerRef : undefined}>
            <button
              className="min-w-[7rem] rounded-md bg-dataops-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-dataops-primary-hover disabled:bg-gray-400"
              onClick={() => props.customButtonAction?.()}
              disabled={props.isLoading === true}
              onMouseLeave={props.tooltip !== undefined ? () => hideTooltip() : undefined}
              onMouseMove={
                props.tooltip !== undefined
                  ? (event) => {
                      const eventSvgCoords = localPoint(event);
                      showTooltip({
                        tooltipData: { description: props.tooltip as string },
                        tooltipTop: eventSvgCoords?.y,
                        tooltipLeft: eventSvgCoords?.x,
                      });
                    }
                  : undefined
              }
            >
              <div className="relative">
                {props.customButtonLabel}
                {props.tooltip !== undefined && (
                  <span className="absolute right-[0.3rem] bottom-2 ml-1 border-[1px] border-white text-white w-3 h-3 rounded-full flex items-center justify-center text-[10px]">
                    i
                  </span>
                )}
              </div>
            </button>
            {tooltipOpen && tooltipData !== undefined && props.tooltip !== undefined && (
              <TooltipInPortal top={tooltipTop} left={tooltipLeft} style={tooltipStyles}>
                <div className="text-sm">{tooltipData.description}</div>
              </TooltipInPortal>
            )}
          </div>
        )}
        {props.customButtonLabel === undefined && props.finishEarlyAvailable !== true && (
          <button
            className="min-w-[7rem] rounded-md bg-dataops-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-dataops-primary-hover disabled:bg-gray-400"
            onClick={() => {
              navigate(location.pathname);
              props.onContinue();
            }}
            disabled={props.continueDisabled === true || props.isLoading === true}
          >
            {props.isLastStep === true ? 'Finish' : 'Continue'}
          </button>
        )}
        {props.customButtonLabel === undefined &&
          props.finishEarlyAvailable === true &&
          props.onFinishEarly !== undefined && (
            <>
              <button
                className="min-w-[7rem] rounded-md bg-dataops-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-dataops-primary-hover disabled:bg-gray-400"
                onClick={() => {
                  navigate(location.pathname);
                  props.onContinue();
                }}
                disabled={props.continueDisabled === true || props.isLoading === true}
              >
                {props.finishEarlyContinueButtonText}
              </button>
              <button
                className="min-w-[7rem] rounded-md bg-dataops-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-dataops-primary-hover disabled:bg-gray-400"
                onClick={() => props.onFinishEarly?.()}
                disabled={props.continueDisabled === true || props.isLoading === true}
              >
                Finish
              </button>
            </>
          )}
        {props.isLoading === true && (
          <div className="absolute right-10 min-h-6 flex items-center justify-center">
            <div
              className="animate-spin inline-block w-6 h-6 border-[2px] border-current border-t-transparent text-dataops-primary rounded-full"
              role="status"
              aria-label="loading"
            >
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        )}
        {props.isFailed === true && props.errorMessage !== undefined && props.isLoading !== true && (
          <div className="absolute right-10 min-h-6 flex items-center">
            <div className="text-red-500 mr-1 flex items-center justify-center" role="status" aria-label="error">
              <ExclamationCircleIcon className="w-5 h-5" />
            </div>
            <span
              className="text-sm text-red-500 whitespace-nowrap overflow-hidden text-ellipsis max-w-[120px] xl:max-w-[250px] wide:max-w-[350px] cursor-pointer hover:text-red-600 transition-colors duration-100"
              title={props.errorMessage}
              onClick={() => {
                const mainContent = document.querySelector('.grow.overflow-y-scroll');
                if (mainContent !== null) {
                  mainContent.scrollTo({ top: mainContent.scrollHeight, behavior: 'smooth' });
                }
              }}
            >
              <span className="font-semibold">Request failed:</span> {props.errorMessage}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
